import { obtenerMensajeError } from "utils/apiErrors";
import { useState, useEffect, useCallback } from "react";
import NotificationsApi from "api/notificationsApi";

const useNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [socket, setSocket] = useState(null);
  const [socketStatus, setSocketStatus] = useState("disconnected");

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const data = await NotificationsApi.getLatestNotifications();
      setNotifications(Array.isArray(data) ? data : []);
      const unreadNotifications = data.filter((notif) => !notif.read).length;
      setUnreadCount(unreadNotifications);
      setError(null);
    } catch (error) {
      console.error("Error obteniendo las Notificaciones:", error);
      setError(obtenerMensajeError(error, "Error al cargar las Notificaciones"));
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleNewNotification = useCallback((notification) => {
    console.log("Nueva notificación recibida:", notification);
    setNotifications((prev) => {
      const exists = prev.some((n) => n.id === notification.id);
      if (exists) return prev;
      return [notification, ...prev].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );
    });
    setUnreadCount((prev) => prev + 1);
  }, []);

  const setupWebSocketConnection = useCallback(async () => {
    try {
      setSocketStatus("connecting");
      const newSocket = await NotificationsApi.setupWebSocket(handleNewNotification);

      if (newSocket) {
        newSocket.onopen = () => {
          console.log("🟢 Conexión WebSocket establecida");
          setSocketStatus("connected");
        };

        newSocket.onclose = async (event) => {
          console.log("🔴 Conexión WebSocket cerrada:", event.code, event.reason);
          setSocketStatus("disconnected");

          // Intentar reconectar si no fue un cierre intencional
          if (event.code !== 1000) {
            setTimeout(() => {
              console.log("🔄 Intentando reconectar...");
              setupWebSocketConnection();
            }, 5000);
          }
        };

        newSocket.onerror = (error) => {
          console.error("❌ Error en WebSocket:", error);
          setSocketStatus("error");
        };

        setSocket(newSocket);
      } else {
        setSocketStatus("error");
        console.error("No se pudo establecer la conexión WebSocket");
      }
    } catch (error) {
      console.error("Error configurando WebSocket:", error);
      setSocketStatus("error");
    }
  }, [handleNewNotification]);

  useEffect(() => {
    fetchNotifications();
    setupWebSocketConnection();

    return () => {
      if (socket) {
        // Cierre limpio del socket
        socket.close(1000, "Componente desmontado");
        setSocket(null);
        setSocketStatus("disconnected");
      }
    };
  }, [fetchNotifications, setupWebSocketConnection]);

  const handleMarkAsRead = useCallback(async (id) => {
    try {
      await NotificationsApi.markAsRead(id);
      setNotifications((prev) =>
        prev.map((notif) => (notif.id === id ? { ...notif, read: true } : notif))
      );
      setUnreadCount((prevCount) => Math.max(0, prevCount - 1));
    } catch (error) {
      console.error("Error marcando la Notificación como leída:", error);
      setError(obtenerMensajeError(error, "Error al marcar la Notificación como leída"));
    }
  }, []);

  const handleMarkAllAsRead = useCallback(async () => {
    try {
      await NotificationsApi.markAllAsRead();
      setNotifications((prev) => prev.map((notif) => ({ ...notif, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error("Error marcando todas las Notificaciones como leídas:", error);
      setError(obtenerMensajeError(error, "Error al marcar todas las Notificaciones como leídas"));
    }
  }, []);

  useEffect(() => {
    const unreadNotifications = notifications.filter((notif) => !notif.read).length;
    if (unreadCount !== unreadNotifications) {
      setUnreadCount(unreadNotifications);
    }
  }, [notifications]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    handleMarkAsRead,
    handleMarkAllAsRead,
    refetch: fetchNotifications,
    socketStatus,
    socketConnected: socketStatus === "connected",
  };
};

export default useNotifications;
